import React, { useState, useEffect } from 'react';
import { 
  Coins, Search, Send, User, RefreshCw, CheckCircle, 
  XCircle, AlertCircle, History, Sparkles, TrendingUp,
  ShieldCheck, ArrowUpRight, ArrowDownLeft, Clock, Award, 
  Building2, Radio, Check, ChevronRight, HelpCircle
} from 'lucide-react';
import { sellerAPI } from '../utils/api';

const SellerDashboard = () => {
  // Stats & Profile
  const [stats, setStats] = useState({
    current_balance: 0,
    total_sold: 0,
    total_transfers: 0,
    today_sold: 0,
    today_transfers: 0,
    seller_level: 'BRONZE',
    commission_percentage: 10
  });
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Transfer Target Mode: 'agency' | 'host'
  const [transferMode, setTransferMode] = useState('agency');

  // Recipient Search State
  const [recipientQuery, setRecipientQuery] = useState('');
  const [searchingRecipient, setSearchingRecipient] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);

  // Transfer Coins State
  const [transferAmount, setTransferAmount] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferSuccessMsg, setTransferSuccessMsg] = useState('');
  const [transferErrorMsg, setTransferErrorMsg] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Transactions History State
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const [txFilter, setTxFilter] = useState('ALL'); // 'ALL' | 'AGENCY' | 'HOST'
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDashboardData();
    fetchTransactions();
  }, []);

  // Real-time debounce search recipient
  useEffect(() => {
    if (!recipientQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => {
      searchTargetRecipient(recipientQuery.trim(), transferMode);
    }, 300);
    return () => clearTimeout(timer);
  }, [recipientQuery, transferMode]);

  const fetchDashboardData = async () => {
    setRefreshing(true);
    try {
      const [statsRes, profileRes] = await Promise.all([
        sellerAPI.getDashboardStats(),
        sellerAPI.getProfile()
      ]);
      if (statsRes.data?.success) {
        setStats(statsRes.data.data);
      }
      if (profileRes.data?.success) {
        setSeller(profileRes.data.data);
      }
    } catch (err) {
      console.error("Error loading seller dashboard:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchTransactions = async () => {
    setLoadingTx(true);
    try {
      const res = await sellerAPI.getTransactions({ search: searchTerm });
      if (res.data?.success) {
        setTransactions(res.data.data || []);
      }
    } catch (err) {
      console.error("Error loading transactions:", err);
    } finally {
      setLoadingTx(false);
    }
  };

  const searchTargetRecipient = async (query, mode) => {
    setSearchingRecipient(true);
    try {
      const res = await sellerAPI.searchRecipients(query, mode);
      if (res.data?.success) {
        setSearchResults(res.data.data || []);
      }
    } catch (err) {
      console.error("Search recipient error:", err);
    } finally {
      setSearchingRecipient(false);
    }
  };

  // 🔹 Comma Formatting Helpers
  const formatNumberWithCommas = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const numStr = String(val).replace(/,/g, '').replace(/[^0-9]/g, '');
    if (!numStr) return '';
    return Number(numStr).toLocaleString('en-IN');
  };

  const getRawNumber = (val) => {
    if (!val) return 0;
    const numStr = String(val).replace(/,/g, '').replace(/[^0-9]/g, '');
    return Number(numStr) || 0;
  };

  const formatAmountInWords = (num) => {
    const n = Number(num);
    if (!n || n <= 0) return '';
    if (n >= 10000000) {
      const cr = (n / 10000000).toFixed(2).replace(/\.00$/, '');
      return `${cr} Crore Coins (${n.toLocaleString('en-IN')})`;
    }
    if (n >= 100000) {
      const lk = (n / 100000).toFixed(2).replace(/\.00$/, '');
      return `${lk} Lakh Coins (${n.toLocaleString('en-IN')})`;
    }
    if (n >= 1000) {
      const th = (n / 1000).toFixed(2).replace(/\.00$/, '');
      return `${th} Thousand Coins (${n.toLocaleString('en-IN')})`;
    }
    return `${n.toLocaleString('en-IN')} Coins`;
  };

  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
    if (raw === '') {
      setTransferAmount('');
      return;
    }
    setTransferAmount(formatNumberWithCommas(raw));
  };

  const setQuickAmount = (val) => {
    setTransferAmount(formatNumberWithCommas(val));
  };

  const handleSelectRecipient = (recipient) => {
    setSelectedRecipient(recipient);
    setRecipientQuery(recipient.name);
    setSearchResults([]);
    setTransferErrorMsg('');
  };

  const handleModeChange = (mode) => {
    setTransferMode(mode);
    setSelectedRecipient(null);
    setRecipientQuery('');
    setSearchResults([]);
    setTransferErrorMsg('');
    setTransferSuccessMsg('');
  };

  const handleExecuteTransfer = async () => {
    const rawAmt = getRawNumber(transferAmount);
    if (!selectedRecipient || rawAmt <= 0) {
      setTransferErrorMsg(`Please select a valid ${transferMode === 'agency' ? 'Agency' : 'Host'} and enter a coin amount.`);
      return;
    }

    if (rawAmt > stats.current_balance) {
      setTransferErrorMsg(`Insufficient Balance! You have ${stats.current_balance.toLocaleString('en-IN')} Coins.`);
      return;
    }

    setTransferLoading(true);
    setTransferErrorMsg('');
    setTransferSuccessMsg('');

    try {
      const res = await sellerAPI.transferCoins({
        target_id: selectedRecipient.target_id || selectedRecipient.id,
        recipient_type: transferMode,
        amount: rawAmt,
        note: transferNote.trim()
      });

      if (res.data?.success) {
        setIsConfirmModalOpen(false);
        setTransferSuccessMsg(res.data.message || `Successfully deposited ${rawAmt.toLocaleString('en-IN')} coins to ${selectedRecipient.name}!`);
        setTransferAmount('');
        setTransferNote('');
        setSelectedRecipient(null);
        setRecipientQuery('');
        
        // Refresh balance & transactions
        fetchDashboardData();
        fetchTransactions();
      }
    } catch (err) {
      setTransferErrorMsg(err.response?.data?.message || err.message || "Failed to transfer coins.");
    } finally {
      setTransferLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (txFilter === 'AGENCY') return (t.description || '').toUpperCase().includes('AGENCY');
    if (txFilter === 'HOST') return (t.description || '').toUpperCase().includes('HOST');
    return true;
  });

  return (
    <div className="space-y-8 font-['Outfit']">
      {/* Top Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-amber-400/10 border border-amber-400/30 text-amber-400 tracking-wide">
              <Sparkles size={12} /> OFFICIAL COIN SELLER
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
            Coin Seller Portal
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Directly deposit coins into Agency Wallets and Host Wallets with instant delivery.
          </p>
        </div>

        <button
          onClick={() => { fetchDashboardData(); fetchTransactions(); }}
          disabled={refreshing}
          className="self-start md:self-auto flex items-center gap-2 px-5 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm transition"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin text-amber-400' : ''} />
          <span>Refresh Balance</span>
        </button>
      </div>

      {/* Hero Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* 1. Live Balance Card (Hero) */}
        <div className="md:col-span-2 bg-gradient-to-br from-amber-500/20 via-yellow-500/10 to-amber-950/40 border-2 border-amber-400/30 rounded-[30px] p-6 md:p-8 relative overflow-hidden shadow-2xl shadow-amber-500/10">
          <div className="absolute top-0 right-0 w-48 h-48 bg-amber-400/10 rounded-full blur-3xl pointer-events-none"></div>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-2xl bg-amber-400/20 border border-amber-400/30 flex items-center justify-center">
                <Coins size={22} className="text-amber-400" />
              </div>
              <span className="text-xs font-black tracking-widest text-amber-300 uppercase">Available Seller Coins</span>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-black bg-amber-400 text-slate-950">
              LIVE
            </span>
          </div>

          <div className="flex items-baseline gap-2">
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
              {Number(stats.current_balance).toLocaleString('en-IN')}
            </h2>
            <span className="text-lg font-bold text-amber-400">Coins</span>
          </div>

          <p className="text-xs text-amber-200/70 font-medium mt-2">
            {formatAmountInWords(stats.current_balance)}
          </p>

          <div className="mt-6 pt-5 border-t border-amber-400/20 flex items-center justify-between text-xs text-slate-300">
            <span>Seller Tier: <strong className="text-amber-400 font-extrabold">{stats.seller_level}</strong></span>
            <span>Commission: <strong className="text-white font-extrabold">{stats.commission_percentage}%</strong></span>
          </div>
        </div>

        {/* 2. Today Sales */}
        <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-[30px] p-6 relative overflow-hidden flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-slate-400 mb-3">
              <span className="text-xs font-extrabold uppercase tracking-wider">Today's Transfers</span>
              <div className="w-8 h-8 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400">
                <TrendingUp size={16} />
              </div>
            </div>
            <h3 className="text-2xl font-black text-white">
              {Number(stats.today_sold).toLocaleString('en-IN')}
            </h3>
            <p className="text-xs text-green-400 font-bold mt-1">
              +{stats.today_transfers} Transfers Today
            </p>
          </div>
          <p className="text-[11px] text-slate-400 mt-4">
            Reset every midnight IST
          </p>
        </div>

        {/* 3. Total Sales (All Time) */}
        <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-[30px] p-6 relative overflow-hidden flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-slate-400 mb-3">
              <span className="text-xs font-extrabold uppercase tracking-wider">Total Lifetime Coins</span>
              <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                <Award size={16} />
              </div>
            </div>
            <h3 className="text-2xl font-black text-white">
              {Number(stats.total_sold).toLocaleString('en-IN')}
            </h3>
            <p className="text-xs text-purple-400 font-bold mt-1">
              {stats.total_transfers} Total Deposits
            </p>
          </div>
          <p className="text-[11px] text-slate-400 mt-4">
            Lifetime business volume
          </p>
        </div>
      </div>

      {/* Main Action Section: Dual Tab Transfer Box */}
      <div className="bg-slate-900/70 backdrop-blur-2xl border border-white/10 rounded-[32px] p-6 md:p-10 shadow-2xl relative">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-yellow-400 flex items-center justify-center text-slate-950 font-black">
              <Send size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white">Direct Coin Deposit</h2>
              <p className="text-sm text-slate-400">Select whether you want to deposit coins into an Agency or a Host.</p>
            </div>
          </div>

          {/* Mode Switcher Tabs */}
          <div className="flex bg-slate-950/60 p-1.5 rounded-2xl border border-white/10 self-start">
            <button
              type="button"
              onClick={() => handleModeChange('agency')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition ${
                transferMode === 'agency'
                  ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 shadow-lg shadow-amber-500/20'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Building2 size={16} />
              <span>Deposit to Agency</span>
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('host')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition ${
                transferMode === 'host'
                  ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/20'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Radio size={16} />
              <span>Deposit to Host</span>
            </button>
          </div>
        </div>

        {/* Alerts */}
        {transferSuccessMsg && (
          <div className="mb-6 p-4 rounded-2xl bg-green-500/10 border border-green-500/25 text-green-400 font-bold flex items-center gap-3 animate-in fade-in">
            <CheckCircle size={22} className="shrink-0" />
            <span>{transferSuccessMsg}</span>
          </div>
        )}
        {transferErrorMsg && (
          <div className="mb-6 p-4 rounded-2xl bg-red-500/10 border border-red-500/25 text-red-400 font-bold flex items-center gap-3 animate-in fade-in">
            <AlertCircle size={22} className="shrink-0" />
            <span>{transferErrorMsg}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column: Search & Recipient Selection */}
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-black text-slate-300 uppercase tracking-wider mb-2">
                1. Search {transferMode === 'agency' ? 'Agency (Name / Code / ID)' : 'Host (Name / XOXO ID / Phone)'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                  <Search size={18} />
                </div>
                <input
                  type="text"
                  value={recipientQuery}
                  onChange={(e) => {
                    setRecipientQuery(e.target.value);
                    if (selectedRecipient) setSelectedRecipient(null);
                  }}
                  placeholder={
                    transferMode === 'agency' 
                      ? "Search Agency Name, Agency Code or ID..." 
                      : "Search Host XOXO ID, Name, Phone or Agency..."
                  }
                  className="w-full pl-11 pr-10 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition font-medium text-sm"
                />
                {searchingRecipient && (
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                    <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {/* Search Results Dropdown */}
              {searchResults.length > 0 && (
                <div className="mt-2 bg-[#0c1227] border border-white/10 rounded-2xl p-2 shadow-2xl space-y-1 max-h-64 overflow-y-auto">
                  {searchResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleSelectRecipient(item)}
                      className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition text-left group"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white overflow-hidden ${
                          item.recipient_type === 'agency' 
                            ? 'bg-amber-500/20 border border-amber-500/30 text-amber-400' 
                            : 'bg-pink-500/20 border border-pink-500/30 text-pink-400'
                        }`}>
                          {item.avatar_url ? (
                            <img src={item.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            item.recipient_type === 'agency' ? <Building2 size={18} /> : <Radio size={18} />
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-white text-sm group-hover:text-amber-400 transition">{item.name}</p>
                          <p className="text-xs text-slate-400">{item.sub_title}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] uppercase font-bold text-slate-400">Current Balance:</span>
                        <p className="font-bold text-sm text-white">🪙 {Number(item.balance).toLocaleString('en-IN')}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Recipient Preview Card */}
            {selectedRecipient ? (
              <div className={`border rounded-2xl p-5 animate-in fade-in ${
                transferMode === 'agency' 
                  ? 'bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30'
                  : 'bg-gradient-to-br from-pink-500/10 to-rose-500/10 border-pink-500/30'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-black uppercase tracking-wider flex items-center gap-1.5 ${
                    transferMode === 'agency' ? 'text-amber-300' : 'text-pink-300'
                  }`}>
                    <CheckCircle size={14} /> Confirmed {transferMode === 'agency' ? 'Agency' : 'Host'} Recipient
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedRecipient(null); setRecipientQuery(''); }}
                    className="text-xs text-slate-400 hover:text-white underline font-bold"
                  >
                    Change
                  </button>
                </div>

                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-xl overflow-hidden border ${
                    transferMode === 'agency' 
                      ? 'bg-amber-400/20 border-amber-400/40 text-amber-300'
                      : 'bg-pink-400/20 border-pink-400/40 text-pink-300'
                  }`}>
                    {selectedRecipient.avatar_url ? (
                      <img src={selectedRecipient.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      transferMode === 'agency' ? <Building2 size={24} /> : <Radio size={24} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-black text-white">{selectedRecipient.name}</h4>
                    <p className="text-xs text-slate-300">{selectedRecipient.sub_title}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                      <span>Live Wallet: <strong className="text-amber-400 font-bold">🪙 {Number(selectedRecipient.balance).toLocaleString('en-IN')}</strong></span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-white/10 rounded-2xl p-6 text-center text-slate-400 text-xs flex flex-col items-center justify-center gap-2">
                {transferMode === 'agency' ? <Building2 size={28} className="text-slate-600" /> : <Radio size={28} className="text-slate-600" />}
                <p>Search and select an {transferMode === 'agency' ? 'Agency' : 'Host'} from above to begin deposit.</p>
              </div>
            )}
          </div>

          {/* Right Column: Amount & Action */}
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-black text-slate-300 uppercase tracking-wider mb-2">
                2. Enter Coin Deposit Amount
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-amber-400 font-bold">
                  🪙
                </div>
                <input
                  type="text"
                  value={transferAmount}
                  onChange={handleAmountChange}
                  placeholder="e.g. 500,000"
                  className="w-full pl-11 pr-24 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition font-black text-xl tracking-wide"
                />
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-400 text-xs font-bold">
                  COINS
                </div>
              </div>

              {/* Amount in words helper */}
              {transferAmount && (
                <p className="text-xs text-amber-400 font-bold mt-2 pl-1">
                  ⚡ {formatAmountInWords(getRawNumber(transferAmount))}
                </p>
              )}

              {/* Quick Amount Chips */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
                {[50000, 100000, 500000, 1000000, 5000000, 10000000].map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setQuickAmount(amt)}
                    className="px-2 py-2 rounded-xl bg-white/5 hover:bg-amber-400/20 border border-white/10 hover:border-amber-400/30 text-white hover:text-amber-300 font-bold text-xs transition text-center"
                  >
                    +{amt >= 1000000 ? `${amt / 1000000}M` : `${amt / 1000}K`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-black text-slate-300 uppercase tracking-wider mb-2">
                3. Deposit Note / Reference (Optional)
              </label>
              <input
                type="text"
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
                placeholder={`e.g. Monthly ${transferMode === 'agency' ? 'Agency' : 'Host'} Coin Allocation`}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition font-medium text-sm"
              />
            </div>

            <button
              type="button"
              disabled={!selectedRecipient || !transferAmount || transferLoading}
              onClick={() => setIsConfirmModalOpen(true)}
              className={`w-full py-4 rounded-2xl font-black text-base transition flex items-center justify-center gap-2 shadow-xl ${
                !selectedRecipient || !transferAmount || transferLoading
                  ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                  : transferMode === 'agency'
                  ? 'bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-slate-950 shadow-amber-500/20'
                  : 'bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white shadow-pink-500/20'
              }`}
            >
              <Send size={18} />
              <span>Deposit to {transferMode === 'agency' ? 'Agency' : 'Host'} Now</span>
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-slate-900 border border-white/20 rounded-[32px] p-6 md:p-8 max-w-md w-full shadow-2xl relative animate-in zoom-in-95">
            <div className="w-14 h-14 rounded-2xl bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-400 mx-auto mb-4">
              <Coins size={28} />
            </div>

            <h3 className="text-2xl font-black text-white text-center">
              Confirm Coin Deposit
            </h3>
            <p className="text-xs text-slate-400 text-center mt-1 mb-6">
              Please double check the recipient and amount before finalizing.
            </p>

            <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-4 space-y-3 mb-6">
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Recipient Type:</span>
                <span className="font-extrabold uppercase text-amber-400">{transferMode}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Target Name:</span>
                <span className="font-bold text-white">{selectedRecipient?.name}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Deposit Amount:</span>
                <span className="font-black text-amber-400 text-sm">
                  🪙 {formatNumberWithCommas(transferAmount)} Coins
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Amount In Words:</span>
                <span className="font-bold text-slate-300 text-[11px] text-right">
                  {formatAmountInWords(getRawNumber(transferAmount))}
                </span>
              </div>
              <div className="pt-2 border-t border-white/10 flex justify-between text-xs">
                <span className="text-slate-400">Balance After Deposit:</span>
                <span className="font-extrabold text-white">
                  🪙 {(stats.current_balance - getRawNumber(transferAmount)).toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setIsConfirmModalOpen(false)}
                disabled={transferLoading}
                className="flex-1 py-3.5 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-sm transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteTransfer}
                disabled={transferLoading}
                className={`flex-1 py-3.5 rounded-xl font-black text-sm transition flex items-center justify-center gap-2 ${
                  transferMode === 'agency'
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950'
                    : 'bg-gradient-to-r from-pink-500 to-rose-500 text-white'
                }`}
              >
                {transferLoading ? (
                  <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Check size={18} />
                    <span>Confirm & Deposit</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History Section */}
      <div className="bg-slate-900/70 backdrop-blur-2xl border border-white/10 rounded-[32px] p-6 md:p-10 shadow-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-black text-white flex items-center gap-2">
              <History size={22} className="text-amber-400" />
              Deposit History & Activity
            </h2>
            <p className="text-xs text-slate-400 mt-1">Audit log of all coins deposited into agencies and hosts.</p>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex bg-slate-950/60 p-1 rounded-xl border border-white/10">
              {['ALL', 'AGENCY', 'HOST'].map((f) => (
                <button
                  key={f}
                  onClick={() => setTxFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    txFilter === f ? 'bg-amber-400 text-slate-950' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <button
              onClick={fetchTransactions}
              className="p-2 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 text-slate-300 transition"
              title="Refresh ledger"
            >
              <RefreshCw size={16} className={loadingTx ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {loadingTx ? (
          <div className="p-16 text-center">
            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-slate-400 text-sm">Loading transactions...</p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="p-16 text-center text-slate-500 text-sm">
            <Coins className="mx-auto mb-3 opacity-30" size={40} />
            <p>No deposit transactions found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10 text-slate-400 text-xs font-black uppercase tracking-wider">
                  <th className="py-4 px-4">Date & Time</th>
                  <th className="py-4 px-4">Type</th>
                  <th className="py-4 px-4">Description</th>
                  <th className="py-4 px-4 text-right">Amount (Coins)</th>
                  <th className="py-4 px-4 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-sm">
                {filteredTransactions.map((tx) => {
                  const isDeposit = tx.transaction_type === 'SELLER_DEPOSIT' || tx.transaction_type === 'COIN_DEDUCT';
                  const isAgency = (tx.description || '').toUpperCase().includes('AGENCY');

                  return (
                    <tr key={tx.id} className="hover:bg-white/5 transition">
                      <td className="py-4 px-4 text-slate-400 text-xs font-medium">
                        {tx.created_at ? new Date(tx.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${
                          isAgency
                            ? 'bg-amber-400/10 text-amber-300 border border-amber-400/20'
                            : 'bg-pink-400/10 text-pink-300 border border-pink-400/20'
                        }`}>
                          {isAgency ? <Building2 size={12} /> : <Radio size={12} />}
                          {isAgency ? 'Agency Deposit' : 'Host Deposit'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-slate-300 font-medium">
                        {tx.description || 'Coin transfer'}
                      </td>
                      <td className="py-4 px-4 text-right font-black text-amber-400">
                        -🪙 {Number(tx.amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase bg-green-500/10 text-green-400 border border-green-500/20">
                          {tx.status || 'COMPLETED'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerDashboard;
